import { cn } from '@/lib/utils';
import { lineCapText, PacingTag } from './LineKpisTable';
import { dayMonth, eur, num } from './format';
import { frequencyLabel } from './labels';
import type { ReportLine, StructureCampaign, StructureIo } from './types';

interface CampaignStructureProps {
  structure: StructureCampaign[];
  lines: ReportLine[];
}

/** DV360 structure grouped campaign → insertion order → line items. */
export function CampaignStructure({ structure, lines }: CampaignStructureProps) {
  const linesById = new Map(lines.map((line) => [line.id, line]));

  if (structure.length === 0) {
    return <p className="text-adv-body-s text-adv-tertiary py-2">Sin estructura en el manifiesto de DV360.</p>;
  }

  return (
    <div className="space-y-6">
      {structure.map((campaign) => (
        <div key={campaign.id} className="space-y-3">
          <div>
            <p className="adv-kpi__label">Campaign</p>
            <p className="font-bold text-adv-primary">{campaign.name}</p>
            <p className="text-adv-body-s text-adv-secondary">
              <span className="adv-num">{campaign.id}</span> · {campaign.goal}
              {campaign.kpi.trim() && (
                <>
                  {' · KPI '}
                  <span className="adv-num">{campaign.kpi}</span>
                </>
              )}
            </p>
          </div>
          {campaign.ios.map((io) => (
            <InsertionOrderBlock key={io.id} io={io} linesById={linesById} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** IO budgets come in impressions or in euros. */
function ioAmount(io: StructureIo, value: number | null): string {
  return io.unit === 'impresiones' ? `${num(value)} impr.` : eur(value, 0);
}

function InsertionOrderBlock({
  io,
  linesById,
}: {
  io: StructureIo;
  linesById: Map<string, ReportLine>;
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] gap-3 pl-4 border-l-2 border-adv-border-default">
      <div className="bg-adv-sunken rounded-adv-lg p-4 space-y-1 text-adv-body-s">
        <p className="adv-kpi__label">Insertion order · {io.status}</p>
        <p className="font-bold text-adv-body-m text-adv-primary break-words">{io.name}</p>
        <p className="adv-num text-adv-tertiary">{io.id}</p>
        <p className="text-adv-secondary">
          <PacingTag type={io.pacing} period="diario" />
        </p>
        {io.segment && (
          <p className="text-adv-secondary">
            Tramo <span className="adv-num text-adv-primary">{ioAmount(io, io.segment.budget)}</span>{' '}
            <span className="adv-num">
              {dayMonth(io.segment.start)} – {dayMonth(io.segment.end)}
            </span>
            {' · queda '}
            <span className="adv-num text-adv-primary">{ioAmount(io, io.segment.remaining)}</span>
          </p>
        )}
        <p className="text-adv-secondary">
          Tope{' '}
          <span className="adv-num text-adv-primary">
            {io.daily_max ? `${eur(io.daily_max, 0)}/día` : 'sin tope diario'}
          </span>
          {' · frecuencia '}
          <span className="adv-num text-adv-primary">{frequencyLabel(io.freq)}</span>
        </p>
      </div>

      <div className="space-y-2">
        {io.lines.map((lineId) => {
          const line = linesById.get(lineId);
          return line ? <LineNode key={lineId} line={line} /> : null;
        })}
      </div>
    </div>
  );
}

function LineNode({ line }: { line: ReportLine }) {
  const isActive = line.status === 'Activa';
  return (
    <div
      className={cn(
        'border border-adv-border rounded-r-adv-md border-l-4 px-3 py-2 bg-adv-card',
        isActive ? 'border-l-adv-gold' : 'border-l-adv-border-strong opacity-70'
      )}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-bold text-adv-body-s text-adv-primary">{line.name}</span>
        <span className={cn('adv-tag', isActive ? 'adv-tag--success' : 'adv-tag--default')}>{line.status}</span>
      </div>
      <p className="text-adv-body-s text-adv-tertiary truncate" title={line.target}>
        {line.target}
      </p>
      <p className="text-adv-body-s text-adv-secondary flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
        <PacingTag type={line.pacing.type} period={line.pacing.period} />
        <span>· {line.goal}</span>
        <span>
          · tope <span className="adv-num text-adv-primary">{lineCapText(line)}</span>
        </span>
        <span>
          · techo <span className="adv-num text-adv-primary">{eur(line.ceiling)}</span>
        </span>
      </p>
    </div>
  );
}
